/**
 * Lobby Store - Game room state management (v2 API)
 *
 * Parameterized by gameType. When gameType changes, the store
 * resets and re-fetches the game list.
 *
 * SECURITY: Authentication is handled via httpOnly cookies.
 * No tokens are stored or passed explicitly.
 */

import { useSyncExternalStore } from "react";
import type { RouteMode } from "../../router";
import {
  createGameApi,
  type GameApi,
  type GameRoom,
  type ResolvedShareLink,
  type ShareLink,
} from "../services/game-api";

/** Lobby state */
interface LobbyState {
  readonly gameType: string | null;
  readonly routeMode: RouteMode;
  readonly games: GameRoom[];
  /** Rooms the current user is already a participant in (rejoin candidates). */
  readonly myGames: GameRoom[];
  readonly currentRoom: GameRoom | null;
  readonly currentShareLink: ShareLink | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

/** Initial state */
const initialState: LobbyState = {
  gameType: null,
  routeMode: "v2",
  games: [],
  myGames: [],
  currentRoom: null,
  currentShareLink: null,
  isLoading: false,
  error: null,
};

/** Current state */
let state: LobbyState = { ...initialState };
let api: GameApi | null = null;

/** Listeners */
const listeners = new Set<() => void>();

/** Notify listeners */
function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

function getApi(): GameApi {
  if (!api) {
    throw new Error("Lobby store not initialized: call setGameType first");
  }
  return api;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * Wraps an async API call with loading/error state management.
 * On success, applies successUpdater to merge result into state.
 * Returns the data on success, or null/false on failure.
 */
async function withLoading<T>(
  apiFn: () => Promise<{ success: boolean; data?: T; error?: string }>,
  fallbackError: string,
  successUpdater: (data: T) => Partial<LobbyState>,
): Promise<T | null> {
  state = { ...state, isLoading: true, error: null };
  notify();

  try {
    const response = await apiFn();

    if (response.success && response.data) {
      state = {
        ...state,
        ...successUpdater(response.data),
        isLoading: false,
      };
      notify();
      return response.data;
    }

    state = {
      ...state,
      isLoading: false,
      error: response.error ?? fallbackError,
    };
  } catch (error) {
    state = {
      ...state,
      isLoading: false,
      error: getErrorMessage(error, fallbackError),
    };
  }
  notify();
  return null;
}

/** Lobby actions */
export const lobbyActions = {
  /** Set active game type -- resets state and creates new API client */
  setGameType: (gameType: string, routeMode: RouteMode = "v2"): void => {
    if (state.gameType === gameType && state.routeMode === routeMode) return;
    api = createGameApi(gameType);
    state = { ...initialState, gameType, routeMode };
    notify();
  },

  /** Fetch available rooms */
  fetchGames: async (): Promise<void> => {
    await withLoading(
      () => getApi().listGames(),
      "게임 목록을 불러올 수 없습니다",
      (games) => ({ games }),
    );
  },

  /** Fetch rooms the current user is a participant in (rejoin list). */
  fetchMyGames: async (): Promise<void> => {
    try {
      const response = await getApi().listMyGames();
      if (response.success && response.data) {
        state = { ...state, myGames: response.data };
        notify();
      }
    } catch {
      // Non-fatal — main lobby list still works
    }
  },

  /** Create a new game room */
  createGame: async (
    config: Record<string, unknown> = {},
  ): Promise<GameRoom | null> => {
    return withLoading(
      () => getApi().createGame(config),
      "게임을 생성할 수 없습니다",
      (room) => ({ currentRoom: room }),
    );
  },

  /** Join a room */
  joinGame: async (roomId: string): Promise<GameRoom | null> => {
    return withLoading(
      () => getApi().joinGame(roomId),
      "게임에 참가할 수 없습니다",
      (room) => ({ currentRoom: room }),
    );
  },

  /** Join a room as a read-only observer. */
  joinObserver: async (roomId: string): Promise<GameRoom | null> => {
    return withLoading(
      () => getApi().joinObserver(roomId),
      "관전자로 입장할 수 없습니다",
      (room) => ({ currentRoom: room }),
    );
  },

  /** Create share link for current waiting room (host only). */
  createShareLink: async (ttlMs?: number): Promise<ShareLink | null> => {
    const currentRoom = state.currentRoom;
    if (!currentRoom) {
      state = { ...state, error: "현재 게임이 없습니다" };
      notify();
      return null;
    }

    return withLoading(
      () => getApi().createShareLink(currentRoom.id, ttlMs),
      "공유 링크를 생성할 수 없습니다",
      (shareLink) => ({ currentShareLink: shareLink }),
    );
  },

  /** Create observer share link for current waiting room (host only). */
  createObserverShareLink: async (
    ttlMs?: number,
  ): Promise<ShareLink | null> => {
    const currentRoom = state.currentRoom;
    if (!currentRoom) {
      state = { ...state, error: "현재 게임이 없습니다" };
      notify();
      return null;
    }

    return withLoading(
      () => getApi().createObserverShareLink(currentRoom.id, ttlMs),
      "관전자 링크를 생성할 수 없습니다",
      (shareLink) => ({ currentShareLink: shareLink }),
    );
  },

  /** Resolve share link token fields (public endpoint). */
  resolveShareLink: async (
    gameType: string,
    roomNumber: string,
    secret: string,
  ): Promise<ResolvedShareLink | null> => {
    return withLoading(
      () => getApi().resolveShareLink(gameType, roomNumber, secret),
      "공유 링크가 유효하지 않습니다",
      () => ({}),
    );
  },

  /** Join room using share-link token fields. */
  joinByShareLink: async (
    gameType: string,
    roomNumber: string,
    secret: string,
  ): Promise<GameRoom | null> => {
    return withLoading(
      () => getApi().joinByShareLink(gameType, roomNumber, secret),
      "공유 링크로 참가할 수 없습니다",
      (room) => ({ currentRoom: room }),
    );
  },

  /** Join room as observer using share-link token fields. */
  joinObserverByShareLink: async (
    gameType: string,
    roomNumber: string,
    secret: string,
  ): Promise<GameRoom | null> => {
    return withLoading(
      () => getApi().joinObserverByShareLink(gameType, roomNumber, secret),
      "관전자 링크로 입장할 수 없습니다",
      (room) => ({ currentRoom: room }),
    );
  },

  /** Start a room (host only) */
  startGame: async (): Promise<boolean> => {
    const currentRoom = state.currentRoom;
    if (!currentRoom) {
      state = { ...state, error: "현재 게임이 없습니다" };
      notify();
      return false;
    }

    const result = await withLoading(
      () => getApi().startGame(currentRoom.id),
      "게임을 시작할 수 없습니다",
      (room) => ({ currentRoom: room }),
    );
    return result !== null;
  },

  /** Cancel current room (host only) */
  cancelGame: async (): Promise<boolean> => {
    const currentRoom = state.currentRoom;
    if (!currentRoom) {
      return false;
    }

    const result = await withLoading(
      () => getApi().cancelGame(currentRoom.id),
      "게임을 취소할 수 없습니다",
      () => ({ currentRoom: null, currentShareLink: null }),
    );
    return result !== null;
  },

  /** Leave current room (calls server API then clears local state) */
  leaveGame: async (): Promise<void> => {
    const currentRoom = state.currentRoom;
    if (currentRoom) {
      try {
        await getApi().leaveGame(currentRoom.id);
      } catch (error) {
        console.error("Failed to leave room via API:", error);
      }
    }
    state = {
      ...state,
      currentRoom: null,
      currentShareLink: null,
      error: null,
    };
    notify();
  },

  /** Set games list directly (called by WS lobby updates) */
  setGames: (games: GameRoom[]): void => {
    state = { ...state, games, isLoading: false };
    notify();
  },

  /** Update current room metadata (called by WS events) */
  updateCurrentRoom: (room: GameRoom): void => {
    state = { ...state, currentRoom: room };
    notify();
  },

  /** Fetch room by ID (for route recovery on page reload) */
  fetchRoom: async (roomId: string): Promise<GameRoom | null> => {
    try {
      const response = await getApi().getRoom(roomId);
      if (response.success && response.data) {
        state = { ...state, currentRoom: response.data };
        notify();
        return response.data;
      }
    } catch {
      // Room not found or not a participant
    }
    return null;
  },

  /** Set current room directly (for route recovery) */
  setCurrentRoom: (room: GameRoom): void => {
    state = { ...state, currentRoom: room };
    notify();
  },

  /** Clear error */
  clearError: (): void => {
    state = { ...state, error: null };
    notify();
  },

  /** Reset to initial state */
  reset: (): void => {
    state = { ...initialState };
    api = null;
    notify();
  },
};

/** Lobby store */
export const lobbyStore = {
  getState: (): LobbyState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/** Hook to access lobby state */
export function useLobby(): LobbyState {
  return useSyncExternalStore(lobbyStore.subscribe, lobbyStore.getState);
}

/** Hook to access lobby actions */
export function useLobbyActions(): typeof lobbyActions {
  return lobbyActions;
}
